import React, { FC, useCallback, useMemo, useState } from 'react'
import styles from 'styles/components/card.module.scss'
import modal from 'styles/components/modal.module.scss'
import text from 'styles/utility/text.module.scss'
import format from 'date-fns/format'
import ja from 'date-fns/locale/ja'
import { useDispatch, useSelector } from 'react-redux'
import { FlexColumn, FlexRow } from 'components/Flex'
import { StockType } from 'types/stock'
import { BlueTag, DisabledTag } from 'components/Tag'
import { HorizontalLine, Spacer } from 'components/Utility'
import { NotificationDescription, SubDescription } from 'components/Typography'
import { CloseButton, OrangeButton, TextButton } from 'components/Button'
import { dateToString, getBreadShortName, getRandomID, parseTimeToDate, sumCheckoutItemsAmount } from 'lib/helper'
import { BasicCard, StockCardRow, StockCardRowEditable } from 'components/Card'
import { FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { closeStockModal, editStockContentInModal, openConfirmModal, openModal } from 'reducks/modal/operations'
import { createStocks, notifyStocks, saveStocksAmount } from 'reducks/stock/operations'
import { ConfirmModal, NotificationModal } from 'components/Modal'
import { State } from 'index'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import {
  getOpenTime,
  getLastOrderTime,
  getStoreName,
  getOrderStartTime,
  getLastPickupTime,
} from 'reducks/store/selectors'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { ModalStockType } from 'types/modal'
import { Role } from 'lib/role'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { DaysOfWeek, DayOfWeekType } from 'lib/daysOfWeek'
import { useIsAfterLastOrder, useIsOrderable, useNextBusinessDate, useTimesWithRange } from 'hooks/store'
import { getOrders } from 'reducks/checkout/selectors'
import { BusinessTimeType, OpenTimeType } from 'types/store'
import { useEarliestBusinessTime, useLatestOpenTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { addDefaultStock, findNotifiedStock } from 'lib/stock'
import {
  useCanBeNotified,
  useHasChangedDeadline,
  useHasChangedStock,
  useHasSamePickupableTime,
  useLastNotifiedMinutes,
  useQueryPrevStocks,
  useRemainingStocksAmount,
  useSetInitialStocks,
} from 'hooks/stock'
import { isBefore, isSameDay } from 'date-fns'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import classNames from 'classnames'
import { SelectBox } from '../Form'
import { Caution } from '../Typography'
const daysOfWeek = new DaysOfWeek()

const StockModal: FC = () => {
  const dispatch = useDispatch()

  const { bread, date, edited, open, schedules, stocks }: ModalStockType = useSelector(
    (state: State) => state.modal.stock
  )
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const orders = useSelector((state: State) => getOrders(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const openTime: OpenTimeType = useSelector((state: State) => getOpenTime(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const lastPickupTime: BusinessTimeType = useSelector((state: State) => getLastPickupTime(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeName = useSelector((state: State) => getStoreName(state))
  const role = useSelector((state: State) => getRole(state))
  const roleManager = new Role(role, storeRef)

  const [stockList, setStockList] = useState<StockType[]>([])
  // 在庫データが存在しないか、前営業日より前に全在庫データが削除扱いになっている
  const isNoStock = stocks.length === 0 || stocks.every((stock) => stock.is_deleted)
  const prevStocks = useQueryPrevStocks(bread, date, storeRef, 'daily')
  const hasChangedStock = useHasChangedStock(prevStocks, stockList, isNoStock)
  const hasChangedDeadline = useHasChangedDeadline(prevStocks, stockList, isNoStock)

  // 翌営業日の日付を取得
  const nextBusinessDate = useNextBusinessDate(closedDates)
  // 曜日を取得
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(date, specialHolidays)
  // 臨時営業日データ（臨時営業日でなければundefined）
  const temporaryOpenCalendar = useGetTemporaryOpen(dateToString(date), storeCalendars)
  // 臨時営業日の引き継ぎ元曜日を取得
  const defaultDayOfWeek = temporaryOpenCalendar?.default_day_of_week || dayOfWeek
  // 当日と翌営業日の営業時間を取得する
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpenCalendar)
  const latestOpenTime = useLatestOpenTime([dayOfWeek], openTime, temporaryOpenCalendar)
  const earliestLastOrderTime = useEarliestBusinessTime(
    [dayOfWeek],
    lastOrderTime,
    'last_order_time',
    temporaryOpenCalendar
  )
  const earliestLastPickupTime = useEarliestBusinessTime(
    [dayOfWeek],
    lastPickupTime,
    'last_pickup_time',
    temporaryOpenCalendar
  )
  // 各時刻を日付型に変換
  const orderStartDateTime = parseTimeToDate(latestOrderStartTime)
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  // 注文締切として選択できる範囲
  const selectableOrderTimes = useTimesWithRange(latestOrderStartTime, earliestLastOrderTime)
  const isDeadlineBeforeNow = isBefore(stockList[0]?.deadline.toDate() || new Date(), new Date())

  // 30分以内に通知を送っているかどうか
  const canBeNotified = useCanBeNotified(stocks)
  const lastNotifiedMinutes = useLastNotifiedMinutes(stocks)
  const _checkoutItems = useCheckoutItemsFromOrders(orders, bread ? bread.bread_ref : undefined)
  const checkoutItems = _checkoutItems.filter((checkoutItem) => checkoutItem.type === 'daily')
  const remainingStockAmount = useRemainingStocksAmount(stockList, checkoutItems)
  const hasSamePickupableTime = useHasSamePickupableTime(stockList)
  const isOrderable = useIsOrderable(date, orderStartDateTime, lastOrderDateTime)
  const isAfterLastOrder = useIsAfterLastOrder(date, lastOrderDateTime)
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, date))
  const strDate = format(isAfterLastOrder || isClosed ? nextBusinessDate : date, 'yyyyMMdd')
  const checkoutItemAmount = sumCheckoutItemsAmount(checkoutItems)

  const isDisabledSaveButton = useMemo(
    () =>
      remainingStockAmount < 0 ||
      (!hasChangedStock && !hasChangedDeadline) ||
      hasSamePickupableTime ||
      (!hasChangedDeadline && isDeadlineBeforeNow),
    [remainingStockAmount, hasChangedStock, hasChangedDeadline, hasSamePickupableTime]
  )

  const isDisabledNotifyButton = useMemo(
    () =>
      hasSamePickupableTime ||
      remainingStockAmount <= 0 ||
      !canBeNotified ||
      !isOrderable ||
      !roleManager.hasEnoughRole(5) ||
      isDeadlineBeforeNow,
    [hasSamePickupableTime, remainingStockAmount, canBeNotified, isOrderable, roleManager]
  )

  const closeStockModalCallback = useCallback(() => {
    if (edited || hasChangedStock || hasChangedDeadline) {
      dispatch(openConfirmModal())
    } else {
      dispatch(closeStockModal())
    }
  }, [edited, hasChangedStock, hasChangedDeadline])

  const handleStock = useCallback(
    (stockRef: FirebaseDocRefType, type: 'plus' | 'minus') => {
      setStockList((prevState) => {
        const index = prevState.findIndex((stock) => stock.stock_ref?.id === stockRef.id)
        // 例外処理...更新対象のstockが存在しない
        if (index === -1) return prevState
        const nextState = prevState
        const nextStock = nextState[index]
        nextState[index].stock = type === 'plus' ? nextStock.stock + 1 : nextStock.stock - 1
        return [...nextState]
      })
    },
    [setStockList, stocks]
  )

  const handleDeadline = useCallback(
    (time: string) =>
      setStockList((prevState) => {
        const nextState = prevState
        nextState.forEach((stock) => {
          stock.deadline = FirebaseTimestamp.fromDate(parseTimeToDate(time, stock.deadline.toDate()))
        })
        return [...nextState]
      }),
    [setStockList]
  )

  const selectPickupableTime = useCallback(
    (stockRef: FirebaseDocRefType, pickupableTime: string) => {
      setStockList((prevState) => {
        const index = prevState.findIndex((stock) => stock.stock_ref?.id === stockRef.id)
        if (index === -1) return prevState
        const schedule = schedules.find((schedule) => schedule.days_of_week.includes(dayOfWeek)) ?? schedules[0]
        if (!schedule) return prevState
        const nextState = prevState
        nextState[index].pickupable_time = pickupableTime
        return [...nextState]
      })
      // 未販売商品のモーダルの場合、編集後に閉じるときの確認モーダルが必要
      if (isNoStock) {
        dispatch(editStockContentInModal())
      }
    },
    [setStockList, stocks, strDate, schedules, dayOfWeek, isAfterLastOrder, isClosed, isNoStock]
  )

  const addStockRow = useCallback(() => {
    if (!bread || !storeRef || !storeName) return
    const schedule = schedules.find((schedule) => schedule.days_of_week.includes(defaultDayOfWeek)) ?? schedules[0]
    if (!schedule) return
    const pickupableTime = latestOpenTime
    const stockRef = storeRef.collection('store_stocks').doc(getRandomID())
    setStockList((prevState) => {
      return [
        ...prevState,
        addDefaultStock(
          bread,
          storeRef,
          schedule,
          stockRef,
          latestOrderStartTime,
          earliestLastOrderTime,
          storeName,
          strDate,
          pickupableTime
        ),
      ]
    })
  }, [
    setStockList,
    bread,
    schedules,
    storeRef,
    storeName,
    stocks,
    defaultDayOfWeek, // 臨時営業日の引き継ぎ元曜日も考慮した曜日を指定
    latestOrderStartTime,
    latestOpenTime,
    earliestLastOrderTime,
    isAfterLastOrder,
    isClosed,
  ])

  const deleteStockRow = useCallback(
    (stockRef: FirebaseDocRefType) => {
      setStockList((prevState) => {
        const nextState = prevState.filter((stock) => stock.stock_ref?.id !== stockRef.id)
        return [...nextState]
      })
    },
    [setStockList]
  )

  // 在庫の初期値を設定する
  useSetInitialStocks(
    bread,
    schedules,
    storeRef,
    storeName,
    stocks,
    setStockList,
    strDate,
    defaultDayOfWeek, // 臨時営業日の引き継ぎ元曜日も考慮した曜日を指定
    latestOrderStartTime,
    latestOpenTime,
    earliestLastOrderTime,
    isAfterLastOrder,
    isClosed
  )

  return (
    <div className={open ? modal.open : ''}>
      <div className={modal.overlay}>
        <div className={modal.overlay__background} onClick={closeStockModalCallback} />
        {open && (
          <div className={modal.overlay__container}>
            <BasicCard
              buttonComponent={<CloseButton onClick={closeStockModalCallback} />}
              dataCy={'stock-modal'}
              title={''}
            >
              {isSameDay(date, new Date()) ? (
                <p className={`${text.font_bold} ${text.font_large}`}>
                  {format(date, 'M月d日（E）に販売中の在庫', { locale: ja })}
                </p>
              ) : (
                <p className={`${text.font_bold} ${text.font_large}`}>
                  {format(date, 'M月d日（E）に予定されている在庫', { locale: ja })}
                </p>
              )}
              <Spacer size={16} />
              <FlexRow justifyContent={'start'}>
                <div className={styles.row__modal__image}>
                  <img src={bread?.image_urls[0] || ''} alt={bread?.name || ''} />
                </div>
                <Spacer size={12} />
                <div>
                  <p className={`${text.font_bold} ${text.font_large}`}>{bread ? getBreadShortName(bread) : ''}</p>
                  <Spacer size={8} />
                  {remainingStockAmount === 0 ? (
                    <DisabledTag size={'large'} tagName={'在庫がありません'} />
                  ) : (
                    <p>
                      在庫数：
                      <BlueTag dataCy={'stock'} shape={'ellipse'} tagName={remainingStockAmount.toLocaleString()} />
                    </p>
                  )}
                  <Spacer size={12} />
                  <SubDescription description={`すでにこの商品が注文された数：${checkoutItemAmount}`} size={'small'} />
                  <Spacer size={8} />
                  <FlexRow justifyContent={'start'}>
                    <div className={classNames(text.font_medium, text.nowrap)}>注文締切</div>
                    <Spacer size={8} />
                    <SelectBox
                      options={selectableOrderTimes}
                      select={handleDeadline}
                      value={format(stockList[0]?.deadline.toDate() || new Date(), 'HH:mm')}
                      size={'small'}
                    />
                  </FlexRow>
                </div>
              </FlexRow>
              <HorizontalLine verticalMargin={12} />
              {stockList.map((stock) => {
                if (!stock.stock_ref) return
                // issue#395 デフォルトの受渡解禁時刻で作成されなかった場合、デフォルトの受渡解禁時刻データにはis_deletedフラグを立てる
                if (stock.is_deleted) return
                return (
                  <React.Fragment key={stock.stock_ref.id}>
                    {isNoStock ? (
                      <StockCardRowEditable
                        deleteStockRow={deleteStockRow}
                        earliestLastPickupTime={earliestLastPickupTime}
                        handleStock={handleStock}
                        latestOpenTime={latestOpenTime}
                        pickupableTime={stock.pickupable_time}
                        selectPickupableTime={selectPickupableTime}
                        stock={stock.stock - sumCheckoutItemsAmount(checkoutItems, stock.pickupable_time)}
                        stockRef={stock.stock_ref}
                      />
                    ) : (
                      <StockCardRow
                        handleStock={handleStock}
                        pickupableTime={stock.pickupable_time}
                        stock={stock.stock - sumCheckoutItemsAmount(checkoutItems, stock.pickupable_time)}
                        stockRef={stock.stock_ref}
                      />
                    )}
                    <HorizontalLine verticalMargin={12} />
                  </React.Fragment>
                )
              })}
              {isNoStock && (
                <FlexColumn alignItems={'center'}>
                  <TextButton iconPath={'/icon/blue/plus.svg'} label={'受渡解禁時刻を追加する'} onClick={addStockRow} />
                  <Spacer size={12} />
                </FlexColumn>
              )}
              {isOrderable && !isClosed && (
                <>
                  {canBeNotified ? (
                    <SubDescription
                      description={'取り置き通知をオンにしているお客さまに取り置きできることを通知します。'}
                    />
                  ) : (
                    <NotificationDescription align={'center'} lastNotifiedMinutes={lastNotifiedMinutes} />
                  )}
                  <Spacer size={12} />
                  <OrangeButton
                    dataCy={'stock-modal'}
                    disabled={isDisabledNotifyButton}
                    iconPath={'/icon/white/bell.svg'}
                    label={'お客さまに通知する'}
                    onClick={async () => {
                      if (isNoStock) {
                        if (!bread) return
                        // 在庫データが存在しない場合は新規作成（Push通知は監視バッチに任せる）
                        const schedule =
                          schedules.find((schedule) => schedule.days_of_week.includes(dayOfWeek)) ?? schedules[0]
                        await dispatch(
                          createStocks({
                            bread,
                            date: strDate,
                            isOrderable,
                            schedule,
                            stocks: stockList,
                          })
                        )
                        const stock = findNotifiedStock(date, stockList, checkoutItems)
                        if (!stock) return
                        // stock_refの追加
                        stock.stock_ref = storeRef
                          .collection('store_stocks')
                          .doc(`stock_daily_${schedule.schedule_ref?.id}-${strDate}_${stock.pickupable_time}`)
                        dispatch(notifyStocks([stock]))
                      } else {
                        // 在庫データが存在する場合は在庫数の変更を保存してPush通知を送る
                        await dispatch(saveStocksAmount(stockList, dateToString(date)))
                        const stock = findNotifiedStock(date, stocks, checkoutItems)
                        if (!stock) return
                        dispatch(notifyStocks([stock]))
                      }
                      dispatch(closeStockModal())
                      dispatch(openModal(<NotificationModal />, '', ''))
                    }}
                    size={'medium'}
                    type={'main'}
                  />
                </>
              )}
              <>
                <Spacer size={12} />
                <OrangeButton
                  dataCy={'stock-modal-save'}
                  disabled={isDisabledSaveButton}
                  label={'保存する'}
                  onClick={async () => {
                    // 保存時に注文締切が現在時刻より前に変更されている場合は在庫を0にする
                    const savedStocks =
                      hasChangedDeadline && isDeadlineBeforeNow
                        ? stockList.map((stock) => ({ ...stock, stock: 0 }))
                        : stockList
                    if (isNoStock) {
                      if (!bread || !storeRef || !storeName) return
                      // 在庫データが存在しない場合は新規作成（Push通知は監視バッチに任せる）
                      const schedule =
                        schedules.find((schedule) => schedule.days_of_week.includes(dayOfWeek)) ?? schedules[0]
                      const pickupableTime = latestOpenTime
                      const stockRef = storeRef.collection('store_stocks').doc(getRandomID())
                      const defaultStock = addDefaultStock(
                        bread,
                        storeRef,
                        schedule,
                        stockRef,
                        latestOrderStartTime,
                        earliestLastOrderTime,
                        storeName,
                        strDate,
                        pickupableTime
                      )
                      await dispatch(
                        createStocks({
                          bread,
                          date: strDate,
                          defaultStock,
                          isOrderable,
                          schedule,
                          stocks: savedStocks,
                        })
                      )
                      dispatch(closeStockModal())
                    } else {
                      // 在庫が存在する場合は閉じた時に保存する
                      await dispatch(saveStocksAmount(savedStocks, dateToString(date)))
                      dispatch(closeStockModal())
                    }
                  }}
                  size={'medium'}
                  type={'main'}
                />
              </>
              {hasChangedDeadline && isDeadlineBeforeNow && (
                <>
                  <Spacer size={20} />
                  <Caution
                    caution={'現在時刻より注文締切が前に設定されているため、保存すると在庫は0になります。'}
                    isBold={false}
                    size={'small'}
                  />
                </>
              )}
              {!hasChangedDeadline && isDeadlineBeforeNow && (
                <>
                  <Spacer size={20} />
                  <Caution
                    caution={'現在時刻より注文締切が前に設定されているため、在庫を追加することができません'}
                    isBold={false}
                    size={'small'}
                  />
                </>
              )}
            </BasicCard>
          </div>
        )}
        <ConfirmModal
          dataCy={'stock-modal'}
          description={'在庫の追加が完了していません。編集した内容が破棄されますが、よろしいでしょうか？'}
          title={'編集内容の破棄'}
        />
      </div>
    </div>
  )
}

export default StockModal
